import React, { useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  CButton,
  CCol,
  CImage,
  CForm,
  CInputGroup,
  CRow,
  CAlert,
} from "@coreui/react";
import bgimage from "../../assets/Images/logo_black.png";
import { BASE_URL } from "../constant_api";


const OTP_LENGTH = 6;

const THEME_COLOR = "#321fdb";
const THEME_COLOR_HOVER = "#2819b3";
const THEME_COLOR_SOFT = "rgba(50, 31, 219, 0.12)";

const otpStyleOverride = `
  .otp-box {
    width: 48px !important;
    height: 56px !important;
    border-radius: 8px !important;
    border: 1.5px solid #d1d5db !important;
    text-align: center;
    font-size: 20px;
    font-weight: 600;
    outline: none;
    color: #1e2433;
    background: #ffffff;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
    padding-block: initial;
  }
`;

const VerifyEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inputRefs = useRef([]);
  const email = searchParams.get("email") || "";
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const digit = value.slice(-1);
    const newOtp = [...otp];
    newOtp[index] = digit;
    setOtp(newOtp);
    setErrorMessage("");

    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, event) => {
    if (event.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (event.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (event.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (event) => {
    event.preventDefault();

    const pastedOtp = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);

    if (!pastedOtp) return;

    const newOtp = Array(OTP_LENGTH)
      .fill("")
      .map((_, index) => pastedOtp[index] || "");

    setOtp(newOtp);
    setErrorMessage("");

    const focusIndex = Math.min(pastedOtp.length, OTP_LENGTH - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  const handleVerify = async (event) => {
    event.preventDefault();

    setErrorMessage("");
    setSuccessMessage("");

    const enteredOtp = otp.join("");

    if (enteredOtp.length !== OTP_LENGTH) {
      setErrorMessage("Please enter the 6-digit verification code.");
      return;
    }

    if (!email) {
      setErrorMessage("Email address is missing.");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${BASE_URL}/user/verify-email-otp`,
        {
          EmailAddress: email.trim().toLowerCase(),
          OTP: enteredOtp,
        }
      );

      if (!response.data?.success) {
        setErrorMessage(response.data?.message || "Unable to verify email.");
        return;
      }

      const userData = response.data?.data || {};
      const token = userData?.token;

      if (!token) {
        setErrorMessage("Email verified, but authentication token was not received.");
        return;
      }

      localStorage.setItem("userDetail", JSON.stringify(response.data.data))
      localStorage.setItem("currentLoginUserId", JSON.stringify(response.data.data.UserID))
      localStorage.setItem("OrganisationID", "")
      setSuccessMessage(response.data?.message || "Email verified successfully.");

      setTimeout(() => {
        navigate("/query", { replace: true });
      }, 800);
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message || "Invalid verification code. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <style>{otpStyleOverride}</style>
      <div className="bg-light min-vh-100 d-flex flex-row align-items-center">
        <div className="lgnauth">
          <div className="lgnform">
            <CRow className="justify-content-center">
              <CCol md={12}>
                {!email ? (
                  <div className="text-center">
                    <h2 className="lgntxt">Email Address Missing</h2>
                    <CAlert color="warning">
                      Please go back to registration and try again.
                    </CAlert>
                    <CButton
                      color="primary"
                      className="px-4 btnsign"
                      style={{ background: THEME_COLOR, borderColor: THEME_COLOR }}
                      onClick={() => navigate("/register")}
                    >
                      Back to Register
                    </CButton>
                  </div>
                ) : (
                  <CForm className="row g-3" noValidate onSubmit={handleVerify}>
                    <h2 className="lgntxt">Verify Your Email</h2>
                    <p style={{ fontSize: "14px", color: "#5b6472", textAlign: "center" }}>
                      Enter the {OTP_LENGTH}-digit code emailed to{" "}
                      <strong>{email}</strong>
                    </p>

                    {errorMessage && (
                      <CAlert
                        color="warning"
                        dismissible
                        onClose={() => setErrorMessage("")}
                      >
                        {errorMessage}
                      </CAlert>
                    )}
                    {successMessage && (
                      <CAlert
                        color="success"
                        dismissible
                        onClose={() => setSuccessMessage("")}
                      >
                        {successMessage}
                      </CAlert>
                    )}

                    <CInputGroup className="mb-2">
                      <div
                        className="single-input-field otpsssss"
                        style={{
                          display: "flex",
                          gap: 8,
                          justifyContent: "center",
                          width: "100%",
                          padding: "8px 0",
                        }}
                        onPaste={handleOtpPaste}
                      >
                        {otp.map((digit, i) => (
                          <input
                            key={i}
                            ref={(el) => (inputRefs.current[i] = el)}
                            id={`otp-${i}`}
                            value={digit}
                            onChange={(e) => handleOtpChange(i, e.target.value)}
                            onKeyDown={(e) => handleOtpKeyDown(i, e)}
                            inputMode="numeric"
                            maxLength={1}
                            disabled={loading}
                            autoComplete={i === 0 ? "one-time-code" : "off"}
                            onFocus={(e) => {
                              e.target.style.borderColor = THEME_COLOR;
                              e.target.style.boxShadow = `0 0 0 3px ${THEME_COLOR_SOFT}`;
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = "#d1d5db";
                              e.target.style.boxShadow = "none";
                            }}
                            className="otp-box"
                          />
                        ))}
                      </div>
                    </CInputGroup>

                    <CButton
                      color="primary"
                      className="px-4 btnsign"
                      type="submit"
                      disabled={loading}
                      style={{ background: THEME_COLOR, borderColor: THEME_COLOR }}
                      onMouseEnter={(e) => {
                        if (!loading) e.target.style.background = THEME_COLOR_HOVER;
                      }}
                      onMouseLeave={(e) => {
                        if (!loading) e.target.style.background = THEME_COLOR;
                      }}
                    >
                      {loading ? "Verifying..." : "Verify Email"}
                    </CButton>

                    <div className="text-center mt-2">
                      <span style={{ fontSize: "14px", color: "#1e2433" }}>
                        Wrong details?{" "}
                        <span
                          style={{
                            color: THEME_COLOR,
                            fontWeight: 600,
                            cursor: "pointer",
                            textDecoration: "underline",
                          }}
                          onClick={() => navigate("/register")}
                        >
                          Back to Register
                        </span>
                      </span>
                    </div>
                  </CForm>
                )}
              </CCol>
            </CRow>
          </div>

          <div className="logobg">
            <h2 className="weltxt">Welcome To</h2>
            <CImage
              src={bgimage}
              height="auto"
              align="center"
              width={300}
              className="mainlgo"
            />
            <p className="qqtxt">
              Lorem Ipsum is simply dummy text of the printing and typesetting
              industry. Lorem Ipsum has been the industry's standard dummy text
              ever since the 1500s, when an unknown printer took a galley of type
              and scrambled it to make a type specimen book.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default VerifyEmail;